import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Star, GitBranch, UserCheck, Globe2, BookOpenCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fade, Eyebrow, PageHeader, usePageMeta } from "@/components/SiteLayout";
import { WorkflowStrip } from "@/components/WorkflowStrip";

const ENGAGEMENTS = [
  {
    name: "JCA Evidence Stress Test", scale: "2–4 weeks · Single asset", tag: "Start here",
    ideal: "First-time EU assets",
    outcome: "You know your weaknesses before assessors do.",
    question: "“What will assessors challenge first?”",
    desc: "A fast executive diagnostic. We identify exactly where assessors are likely to challenge your evidence — privately, months before they ask publicly.",
  },
  {
    name: "JCA Readiness Assessment", scale: "4–8 weeks · One indication", tag: "★ Recommended",
    ideal: "Phase III / pre-submission",
    outcome: "A clear, defensible verdict for your leadership team.",
    question: "“If Article 9 started tomorrow — would we pass?”",
    desc: "The full strategic assessment — every dimension of your evidence, synthesized into one board-ready deliverable.",
  },
  {
    name: "JCA Evidence Investment Strategy", scale: "3–6 months · Multi-PICO", tag: "Complex assets",
    ideal: "Complex oncology & multi-indication assets",
    outcome: "Evidence spend prioritized where it changes the outcome.",
    question: "“Where should we invest next — and what can wait?”",
    desc: "The full assessment, extended across every population, subgroup and market that matters.",
  },
  {
    name: "JCA Evidence Monitoring", scale: "Quarterly · Per asset", tag: "Ongoing",
    ideal: "Assets already assessed",
    outcome: "Your risk picture never goes stale.",
    question: "“Has our risk changed since last quarter?”",
    desc: "As data matures, competitors publish and PICOs shift, we reassess your asset each quarter and keep the picture current.",
  },
];

const EVERY_ENGAGEMENT = [
  "AI-assisted evidence analysis",
  "Senior consultant review",
  "Executive presentation",
  "Traceable recommendations",
];

const TIMELINE = [
  { week: "Phase 1", label: "Evidence foundation", desc: "Your documents, trials and competitive landscape ingested and structured against JCA requirements — nothing analysed until the foundation is complete" },
  { week: "Phase 2", label: "Intelligence & analysis", desc: "PICO forecasting, comparator mapping, ITC feasibility and gap scoring — every dimension your engagement covers, at full depth" },
  { week: "Phase 3", label: "Expert validation", desc: "Senior consultants pressure-test every finding against assessor behaviour — iterating with your team until conclusions withstand challenge" },
  { week: "Phase 4", label: "Executive decision", desc: "Verdict, roadmap and decisions — presented and defended live in your governance meeting" },
];

const TRUST_POINTS = [
  { icon: GitBranch, label: "Traceable" },
  { icon: UserCheck, label: "Expert reviewed" },
  { icon: Globe2, label: "Country-specific" },
  { icon: BookOpenCheck, label: "Linked to published evidence" },
  { icon: ShieldCheck, label: "Governance ready" },
];

const RISK_EXAMPLES = [
  { risk: "Missing comparator evidence for anticipated PICOs", sev: "High" },
  { risk: "ESS collapse risk in unanchored MAICs", sev: "High" },
  { risk: "HRQoL / patient-relevant outcome deficiency", sev: "High" },
  { risk: "Programming reproducibility gaps", sev: "Medium" },
  { risk: "Article 9 transparency weaknesses", sev: "Medium" },
  { risk: "National reimbursement risk concentration", sev: "Medium" },
];

export default function Engagements() {
  const navigate = useNavigate();
  usePageMeta(
    "Engagements — Stress Test to Evidence Monitoring | JCA Matrix™",
    "Four engagement models — from a 2–4 week JCA Evidence Stress Test to quarterly per-asset Evidence Monitoring. You are not commissioning a report; you are commissioning a decision."
  );
  return (
    <>
      <PageHeader
        eyebrow="How engagements work"
        title="You are not commissioning a report. You are commissioning a decision."
        intro={<>Every engagement combines proprietary evidence intelligence with senior HEOR expertise to answer one question: <span className="font-semibold text-slate-800">can your evidence withstand JCA scrutiny?</span> The report is simply the output. The decision is the product.</>}
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6" data-testid="engagement-ladder">
            {ENGAGEMENTS.map((e, i) => (
              <motion.div key={e.name} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className={`rounded-2xl p-7 flex flex-col transition-all duration-300 hover:-translate-y-0.5 ${i === 1 ? "border-2 border-blue-600 bg-white shadow-[0_20px_60px_-24px_rgba(37,99,235,0.3)]" : "border border-slate-200 bg-white hover:shadow-md"}`}
                data-testid={`engagement-card-${i}`}>
                <div className="flex items-center justify-between">
                  <span className={`rounded-full px-3 py-1 text-[11px] font-semibold whitespace-nowrap ${i === 1 ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>{e.tag}</span>
                  <span className="text-xs text-slate-400 font-medium text-right">{e.scale}</span>
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold tracking-tight leading-snug">{e.name}</h3>
                <p className="mt-3 text-sm font-medium text-slate-800 italic">{e.question}</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{e.desc}</p>
                <div className="mt-5 rounded-lg bg-emerald-50/70 border border-emerald-100 px-3 py-2 text-xs" data-testid={`engagement-outcome-${i}`}>
                  <span className="font-semibold text-emerald-700">Outcome: </span><span className="text-slate-700">{e.outcome}</span>
                </div>
                <div className="mt-2.5 rounded-lg bg-blue-50/70 border border-blue-100 px-3 py-2 text-xs" data-testid={`engagement-ideal-${i}`}>
                  <span className="font-semibold text-blue-700">Ideal for: </span><span className="text-slate-700">{e.ideal}</span>
                </div>
                <Button onClick={() => navigate("/contact")} variant={i === 1 ? "default" : "outline"} data-testid={`engagement-cta-${i}`}
                  className={`group mt-5 rounded-lg h-10 transition-colors duration-200 ${i === 1 ? "bg-blue-600 hover:bg-blue-700 text-white" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                  Start a conversation <ArrowRight className="ml-1.5 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Button>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 px-6 py-5" data-testid="engagement-note">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Every engagement includes</span>
              {EVERY_ENGAGEMENT.map((t) => (
                <span key={t} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> {t}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">The detailed deliverable breakdown for each engagement is on the <button onClick={() => navigate("/deliverables")} className="underline underline-offset-2 hover:text-blue-600 transition-colors" data-testid="engagement-outcomes-link">Outcomes page</button> — and scoping, deliverables and fees are always defined together in a confidential proposal.</p>
          </div>
        </div>
      </section>

      {/* Typical engagement timeline */}
      <section className="py-24 bg-slate-50 border-y border-slate-200" data-testid="engagement-timeline">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <Eyebrow>How an engagement unfolds</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              A disciplined path from evidence to <span className="text-blue-600">verdict</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">Four phases, whatever the scope. What scales with your engagement is the depth of each phase — no phase is skipped, and none is rushed.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIMELINE.map((t, i) => (
              <motion.div key={t.week} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="relative rounded-2xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/5 transition-all duration-300"
                data-testid={`timeline-week-${i}`}>
                <div className="text-[11px] font-semibold uppercase tracking-widest text-blue-600">{t.week}</div>
                <h3 className="mt-2 font-display text-lg font-semibold tracking-tight">{t.label}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
          <WorkflowStrip className="mt-14" />
        </div>
      </section>

      {/* AI identifies risks */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Eyebrow>Risk intelligence</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              AI surfaces the risks assessors will find — <span className="text-blue-600">before they start looking</span>.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Every gap is scored by severity, mapped to the countries it affects, and paired with a mitigation and
              timeline — then our consultants pressure-test each one against assessor behaviour, simulating the
              questions IQWiG, HAS and JCA statisticians are most likely to ask.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              The result: your team answers the hardest questions privately, months before they are asked publicly.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-xl border-2 border-blue-600 bg-white p-5 mb-5" data-testid="first-jca-stat">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-blue-600">Patterns from the published JCAs</div>
              <p className="mt-2 font-display text-xl font-semibold text-slate-900 leading-snug">Every assessment so far tells the same story: most PICOs go unanswered.</p>
              <p className="mt-1.5 text-sm text-slate-600">In the very first JCA, 8 PICOs were defined — only 1 could be fully answered. Later assessments repeat the pattern. The gap between the evidence companies generate and the evidence the JCA framework expects is structural. Our Stress Test finds it before assessors do.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="risk-list">
              <div className="px-5 py-3 bg-slate-900 text-white text-[11px] font-semibold uppercase tracking-[0.14em]">Typical issues identified during a JCA Evidence Stress Test</div>
              <div className="divide-y divide-slate-100">
                {RISK_EXAMPLES.map((r) => (
                  <div key={r.risk} className="px-5 py-3.5 flex items-center gap-3">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold shrink-0 ${r.sev === "High" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{r.sev}</span>
                    <span className="text-sm text-slate-700">{r.risk}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-20 bg-slate-900" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
            Every recommendation is
          </h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {TRUST_POINTS.map((t, i) => (
              <motion.div key={t.label} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                className="flex flex-col items-center gap-3" data-testid={`trust-point-${i}`}>
                <div className="h-12 w-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                  <t.icon className="h-5 w-5 text-blue-400" strokeWidth={1.75} />
                </div>
                <span className="text-sm font-medium text-slate-200">{t.label}</span>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 font-display text-lg font-semibold text-slate-400">Not AI. <span className="text-blue-400">Confidence.</span></p>
        </div>
      </section>
    </>
  );
}
